import React, { useMemo } from 'react'

import { useCertificateActivityInfinite } from 'hooks/api/useCertificateActivity'
import type { CertificateActivityActor, CertificateActivityItem } from 'services/certificateActivityApi'
import type { ActivityRow } from 'components/display/Activity/Activity'
import { Activity } from 'components'
import { toDollar } from 'utils'

import {ActivityIcon, ArchiveIcon, ArrowRightIcon, BadgePlusIcon, CheckCircleIcon, EyeIcon, MergeIcon, RefreshCwIcon, SplitIcon, TagIcon, TrashIcon, UnlinkIcon} from 'lucide-react'

interface ActivityTabProps {
  certificateId: string
  /** This certificate's own product title (already loaded by CertificatePage) —
   *  threaded through so the label builder can name it in-line ("Added <product>
   *  to group") without an extra fetch. VRG-231 review item 6. */
  productTitle?: string
}

function getActionIcon(type: string): React.ReactNode {
  switch (type) {
    case 'create_certificate':
      return <BadgePlusIcon strokeWidth={1} />
    case 'CertificateStatusChange':
      return <CheckCircleIcon strokeWidth={1} />
    case 'admin_transfer':
    case 'confirm_transfer':
    case 'accept_transfer':
      return <ArrowRightIcon strokeWidth={1} />
    case 'admin_cancel_transfer':
    case 'confirm_request_cancel_transfer':
      return <RefreshCwIcon strokeWidth={1} />
    case 'certificate_archived':
      return <ArchiveIcon strokeWidth={1} />
    case 'certificate_unarchived':
      return <ArchiveIcon strokeWidth={1} />
    case 'tag_replaced':
      return <TagIcon strokeWidth={1} />
    case 'certificate_viewed':
      return <EyeIcon strokeWidth={1} />
    case 'certificate_deleted':
      return <TrashIcon strokeWidth={1} />
    // VRG-232: certificate groups
    // VRG-231 review round 3 — split apart, and off `LayersIcon`. Joining and
    // leaving a group are opposite events and shared one glyph; `LayersIcon`
    // itself now means nothing in the certificates domain (it collided with the
    // indivisible divisibility badge). Same vocabulary as the kebabs and the
    // company activity feed: Merge to join, Unlink to leave, Split to dissolve.
    case 'add_to_group':
      return <MergeIcon strokeWidth={1} />
    case 'remove_from_group':
      return <UnlinkIcon strokeWidth={1} />
    case 'split_group':
      return <SplitIcon strokeWidth={1} />
    default:
      return <ActivityIcon strokeWidth={1} />
  }
}

// VRG-231 review item 6 — the label builder now takes the log's own snapshot
// (item.certificate) and the page's product title into account, instead of being
// a bare per-type string. Two things this unlocks:
//   1. "Added to group" -> "Added <product> to group" (names what happened).
//   2. A group PARENT's own "add_to_group" entry (its create-certificate-group
//      use-case writes ONLY addToGroupAction — there's no separate "parent
//      created" log type today) reads as "Created a certificate group" instead
//      of the generic child-side copy. Detected from the snapshot itself: a
//      parent has group_divisibility set and no parent_certificate_id, exactly
//      the same test CertificatePage uses to derive `groupRole`.
function isGroupParentSnapshot(item: CertificateActivityItem): boolean {
  const snap = item.certificate
  return Boolean(snap && !snap.parent_certificate_id && snap.group_divisibility)
}

function formatActionLabel(item: CertificateActivityItem, productTitle?: string): string {
  const type = item.type
  const product = productTitle?.trim()

  switch (type) {
    case 'create_certificate':
      return isGroupParentSnapshot(item) ? 'Created a certificate group' : 'Created certificate'
    case 'CertificateStatusChange':
      return 'Status changed'
    case 'admin_transfer':
      return 'Initiated transfer'
    case 'admin_cancel_transfer':
      return 'Cancelled transfer'
    case 'confirm_transfer':
      return 'Confirmed transfer'
    case 'confirm_request_cancel_transfer':
      return 'Requested cancel transfer'
    case 'accept_transfer':
      return 'Accepted transfer'
    case 'certificate_archived':
      return 'Archived certificate'
    case 'certificate_unarchived':
      return 'Unarchived certificate'
    case 'tag_replaced':
      return 'Replaced tag'
    case 'certificate_viewed':
      return 'Viewed certificate'
    case 'certificate_deleted':
      return 'Deleted certificate'
    // VRG-232: certificate groups. Written on BOTH sides of a link, so the parent's
    // and the child's Activity tabs each tell the whole story. Today the parent's
    // group-creation moment is ALSO logged as add_to_group (no dedicated
    // "group created" type exists yet — see isGroupParentSnapshot above), so that
    // case is intercepted here before falling through to the child-side copy.
    case 'add_to_group':
      if (isGroupParentSnapshot(item)) return 'Created a certificate group'
      return product ? `Added ${product} to group` : 'Added to group'
    case 'remove_from_group':
      return product ? `Removed ${product} from group` : 'Removed from group'
    case 'split_group':
      return 'Split group'
    default:
      return type.replace(/_/g, ' ')
  }
}

function actorName(a: CertificateActivityActor | null | undefined): string | null {
  return (
    a?.full_name ||
    [a?.first_name, a?.last_name].filter(Boolean).join(' ').trim() ||
    null
  )
}

// Minimal HTML escape — the Activity component renders `result` as HTML via
// html-react-parser, and the recipient name is user-supplied.
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
}

// Build the spread-out row detail — "to <recipient> · <price>" — from the
// server-resolved recipient (user_to) and the price snapshot in metadata. Rendered
// after the action label (e.g. "Initiated transfer  to Jane Smith · $450.00"),
// matching the company activity log's before→after result style. Empty for events
// with no recipient/price (create, cancel, etc.) so they stay single-action rows.
function buildResult(item: CertificateActivityItem): string {
  const parts: string[] = []
  const recipient = actorName(item.user_to)
  if (recipient) parts.push(`to <span>${esc(recipient)}</span>`)
  const raw = item.metadata?.price
  const price = raw == null || raw === '' ? null : Number(raw)
  if (price != null && !Number.isNaN(price) && price > 0) parts.push(`<span>${toDollar(price)}</span>`)
  return parts.join(' &middot; ')
}

function mapItemToRow(item: CertificateActivityItem, productTitle?: string): ActivityRow {
  const user = item.user
  // The API returns first_name / last_name (and role as a plain string) rather than
  // a composed full_name / role object — normalise both so the row shows the actor.
  const roleName =
    typeof user?.role === 'string' ? user.role : user?.role?.name ?? null
  return {
    user: {
      id: user?.id ?? '',
      full_name: actorName(user),
      avatar: user?.avatar ?? null,
      role: roleName ? { name: roleName } : null,
    },
    action: formatActionLabel(item, productTitle),
    result: buildResult(item),
    date: item.created_at,
    icon: getActionIcon(item.type),
  }
}

const ACTIVITY_COLUMNS = [
  { field: 'user.full_name', headerName: 'User', sortable: true },
  { field: 'user.role.name', headerName: 'Role', sortable: true },
  { field: 'action', headerName: 'Action', sortable: true },
  { field: 'date', headerName: 'Date', sortable: true },
]

export const ActivityTab = ({ certificateId, productTitle }: ActivityTabProps) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCertificateActivityInfinite(certificateId)

  // Flatten the loaded pages into a single row list (infinite scroll appends pages).
  const rows: ActivityRow[] = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.data).map((item) => mapItemToRow(item, productTitle)),
    [data, productTitle]
  )

  return (
    <Activity
      activity={rows}
      columns={ACTIVITY_COLUMNS}
      loading={isLoading}
      hasMore={!!hasNextPage}
      loadingMore={isFetchingNextPage}
      onLoadMore={fetchNextPage}
      fillHeight
    />
  )
}
